import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Warning, Trash, MoonStars } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTDOWN_SECONDS = 10;

export function DangerZone() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'deactivate' | 'delete'
  const [password, setPassword] = useState('');
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (mode !== 'delete') return;
    setCountdown(COUNTDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [mode]);

  const close = () => {
    setMode(null);
    setPassword('');
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/profile/deactivate`, { password }, { withCredentials: true });
      setUser(false);
      toast.success(`Account deactivated. Log in before ${new Date(data.delete_after).toLocaleDateString()} to restore it.`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not deactivate account');
    } finally {
      setBusy(false);
    }
  };

  const deleteForever = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/profile/delete`, { password }, { withCredentials: true });
      setUser(false);
      toast.success('Your account and everything you posted has been permanently deleted.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 border-rose-500/30" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-6">
        <Warning size={22} className="text-rose-400 mt-0.5 shrink-0" />
        <div>
          <h3 className="text-2xl tracking-tight font-light">Danger Zone</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Take a break or leave for good. Deactivating hides everything you posted; if you do not log in
            within 30 days your account and content are deleted automatically.
          </p>
        </div>
      </div>

      {mode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setMode('deactivate')}
            className="text-left p-5 rounded-lg border border-amber-400/30 bg-amber-400/5 hover:border-amber-400/60 transition-colors"
            data-testid="open-deactivate-button"
          >
            <MoonStars size={22} className="text-amber-300 mb-2" />
            <p className="text-sm font-medium mb-1">Deactivate for 30 days</p>
            <p className="text-xs text-muted-foreground">
              Your profile, confessions and comments are hidden. Log in any time within 30 days to restore
              everything &mdash; after that it is deleted permanently.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setMode('delete')}
            className="text-left p-5 rounded-lg border border-rose-500/30 bg-rose-500/5 hover:border-rose-500/60 transition-colors"
            data-testid="open-delete-button"
          >
            <Trash size={22} className="text-rose-400 mb-2" />
            <p className="text-sm font-medium mb-1">Delete account permanently</p>
            <p className="text-xs text-muted-foreground">
              Erases your account, confessions, comments, likes and game scores immediately. This can never
              be undone.
            </p>
          </button>
        </div>
      )}

      {mode === 'deactivate' && (
        <div className="space-y-4" data-testid="deactivate-panel">
          <div className="p-4 rounded-lg border border-amber-400/30 bg-amber-400/5 text-xs text-amber-100">
            Everything you posted will be hidden immediately. Log in within 30 days and it all comes back
            exactly as it was. Miss that window and the account is deleted for good.
          </div>
          <div>
            <Label htmlFor="deactivate-password">Confirm your password</Label>
            <Input
              id="deactivate-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="deactivate-password-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={deactivate}
              disabled={busy || !password}
              className="rounded-full bg-amber-500 hover:bg-amber-500/90 text-black"
              data-testid="confirm-deactivate-button"
            >
              {busy ? 'Deactivating...' : 'Deactivate my account'}
            </Button>
            <Button variant="ghost" className="rounded-full" onClick={close} data-testid="cancel-deactivate-button">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'delete' && (
        <div className="space-y-4" data-testid="delete-panel">
          <div className="p-4 rounded-lg border border-rose-500/40 bg-rose-500/10 text-xs text-rose-100" data-testid="delete-warning">
            <p className="font-medium mb-1">This cannot be undone at any cost.</p>
            <p>
              Your account, every confession, comment, like and score will be erased from our servers. We
              will not be able to restore any of it, even if you ask us to.
            </p>
          </div>
          <div>
            <Label htmlFor="delete-password">Confirm your password</Label>
            <Input
              id="delete-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-password-input"
            />
          </div>
          <div className="flex items-center gap-2">
            {countdown > 0 ? (
              <Button disabled className="rounded-full bg-rose-900/60" data-testid="delete-countdown">
                Read the warning... {countdown}s
              </Button>
            ) : (
              <Button
                onClick={deleteForever}
                disabled={busy || !password}
                className="rounded-full bg-rose-600 hover:bg-rose-600/90"
                data-testid="confirm-delete-button"
              >
                {busy ? 'Deleting...' : 'Delete my account forever'}
              </Button>
            )}
            <Button variant="ghost" className="rounded-full" onClick={close} data-testid="cancel-delete-button">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
